import React, { useEffect, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import { useNavigate } from 'react-router-dom';
import
{
    PlusLg,
    XLg,
    Grid3x3GapFill,
    FolderFill,
    ArrowRight
} from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { useAuth } from '../contexts/AuthContext';
import { useGuidedTour } from '../tour/GuidedTourContext';
import { canCreateProject } from '../utils/roles';
import './Dashboard-new.css';

/* ============================================================
   Dashboard-new.tsx
   Redesigned dashboard page with greeting, stats, recent projects.
   ============================================================ */

/* ---------- Type definitions ---------- */

/* Shape of a single recent project from the API */
interface UserRecentProject
{
    project_id: number;
    project_name: string;
    project_organization_name: string | null;
    project_total_docs: number;
    project_total_jobs: number;
    project_total_test_cases: number;
}

/* Shape of the full dashboard API response */
interface DashboardData
{
    user_recent_projects: UserRecentProject[];
    user_total_project_count: number;
    total_documents: number;
    total_jobs: number;
    total_test_cases: number;
}

/* ---------- Helpers ---------- */

/* Return a time-of-day greeting: "Good morning", "Good afternoon", or "Good evening" */
function getTimeGreeting(): string
{
    const hour = new Date().getHours();
    if (hour < 12)
    {
        return 'Good morning';
    }
    if (hour < 18)
    {
        return 'Good afternoon';
    }
    return 'Good evening';
}

/* Extract the first name from a full name string */
function getFirstName(fullName: string | undefined): string
{
    if (!fullName)
    {
        return '';
    }
    return fullName.split(' ')[0];
}

/* ---------- Component: Dashboard ---------- */

function Dashboard(): React.ReactElement
{
    /* Hooks */
    const navigate = useNavigate();
    const { user } = useAuth();
    const { startGuidedTour, isGuidedTourAvailable } = useGuidedTour();
    const canCreateProjectForUser = canCreateProject(user);

    /* State */
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showBanner, setShowBanner] = useState<boolean>(true);

    /* Fetch dashboard data on mount */
    useEffect(() =>
    {
        fetchDashboardData();
    }, [user?.active_organization_id, user?.organization_id]);

    /* ---------- Data fetching ---------- */

    /* Load the dashboard summary from the API */
    async function fetchDashboardData(): Promise<void>
    {
        try
        {
            setLoading(true);
            setError(null);
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_DATA);
            setDashboardData(response.data);
        }
        catch (err)
        {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
        finally
        {
            setLoading(false);
        }
    }

    /* ---------- Event handlers ---------- */

    /* Navigate to create-project page */
    function handleCreateProject(): void
    {
        if (!canCreateProjectForUser)
        {
            return;
        }
        navigate('/projects/new');
    }

    /* Close the info banner */
    function handleCloseBanner(): void
    {
        setShowBanner(false);
    }

    /* Navigate to a project detail page */
    function handleViewProject(projectId: number): void
    {
        navigate(`/projects/${projectId}`);
    }

    /* Navigate to the projects list */
    function handleViewAllProjects(): void
    {
        navigate('/projects');
    }

    /* Relaunch the guided product tour */
    function handleStartGuidedTour(): void
    {
        startGuidedTour();
    }

    /* ---------- Render helpers ---------- */

    /* Render the loading spinner */
    function renderLoading(): React.ReactNode
    {
        return (
            <div className="db-loading">
                <Spinner animation="border" />
            </div>
        );
    }

    /* Render the blue info banner at the top */
    function renderInfoBanner(): React.ReactNode
    {
        if (!showBanner || !isGuidedTourAvailable)
        {
            return null;
        }

        return (
            <div className="db-info-banner">
                <div className="db-info-banner-left">
                    <Grid3x3GapFill size={16} className="db-info-banner-icon" />
                    <span>Need a reminder of how things work?</span>
                    <button
                        id="dashboard-tour-replay"
                        className="db-info-banner-link"
                        type="button"
                        onClick={handleStartGuidedTour}
                    >
                        Start the guided tour →
                    </button>
                </div>
                <button
                    className="db-info-banner-close"
                    type="button"
                    onClick={handleCloseBanner}
                    title="Dismiss"
                >
                    <XLg size={14} />
                </button>
            </div>
        );
    }

    /* Render the greeting header + Create Project button */
    function renderHeader(): React.ReactNode
    {
        const greeting = getTimeGreeting();
        const firstName = getFirstName(user?.name);

        return (
            <div className="db-header">
                <div className="db-greeting-group">
                    <h1 className="db-greeting-title">
                        {greeting}, {firstName}
                    </h1>
                    <p className="db-greeting-subtitle">
                        Track projects, documents, jobs and test cases across your workspace.
                    </p>
                </div>
                {canCreateProjectForUser && (
                    <button
                        id="dashboard-create-project-action"
                        className="db-create-btn"
                        type="button"
                        onClick={handleCreateProject}
                    >
                        <PlusLg size={16} />
                        Create Project
                    </button>
                )}
            </div>
        );
    }

    /* Render the four stats cards */
    function renderStatsCards(): React.ReactNode
    {
        return (
            <div className="db-stats-row">
                {/* Total projects */}
                <div className="db-stat-card">
                    <span className="db-stat-label">Projects</span>
                    <span className="db-stat-value">
                        {dashboardData?.user_total_project_count ?? 0}
                    </span>
                </div>

                {/* Total documents */}
                <div className="db-stat-card">
                    <span className="db-stat-label">Documents</span>
                    <span className="db-stat-value">
                        {dashboardData?.total_documents ?? 0}
                    </span>
                </div>

                {/* Total jobs */}
                <div className="db-stat-card">
                    <span className="db-stat-label">Jobs</span>
                    <span className="db-stat-value">
                        {dashboardData?.total_jobs ?? 0}
                    </span>
                </div>

                {/* Total test cases */}
                <div className="db-stat-card">
                    <span className="db-stat-label">Test Cases</span>
                    <span className="db-stat-value">
                        {dashboardData?.total_test_cases ?? 0}
                    </span>
                </div>
            </div>
        );
    }

    /* Render a single project row in the recent projects table */
    function renderProjectRow(project: UserRecentProject): React.ReactNode
    {
        const showOrganizationColumn = shouldShowOrganizationColumn();
        return (
            <tr key={project.project_id}>
                {/* Project name */}
                <td data-label="Project">
                    <span className="db-project-name">{project.project_name}</span>
                </td>

                {/* Organization — only for superadmin */}
                {showOrganizationColumn && (
                    <td data-label="Organization">
                        <span className="db-repo-name">
                            {project.project_organization_name ?? '—'}
                        </span>
                    </td>
                )}

                {/* Documents */}
                <td className="db-col-center" data-label="Documents">
                    {project.project_total_docs}
                </td>

                {/* Jobs */}
                <td className="db-col-center" data-label="Jobs">
                    {project.project_total_jobs}
                </td>

                {/* Test Cases */}
                <td className="db-col-center" data-label="Test Cases">
                    {project.project_total_test_cases}
                </td>

                {/* View button */}
                <td className="db-col-action" data-label="Actions" data-card-actions="true">
                    <button
                        className="db-view-btn"
                        type="button"
                        onClick={() => handleViewProject(project.project_id)}
                    >
                        View
                    </button>
                </td>
            </tr>
        );
    }

    function shouldShowOrganizationColumn(): boolean
    {
        const isSuperAdmin =
            String((user as any)?.role || '').trim().toLowerCase() === 'superadmin'
            || (user as any)?.is_superuser === true;
        const hasActiveOrganizationScope =
            Number((user as any)?.active_organization_id || (user as any)?.organization_id || 0) > 0;

        return isSuperAdmin && !hasActiveOrganizationScope;
    }

    /* Render the recent projects table card */
    function renderRecentProjects(): React.ReactNode
    {
        const projects = dashboardData?.user_recent_projects ?? [];
        const showOrganizationColumn = shouldShowOrganizationColumn();

        return (
            <div id="dashboard-recent-projects" className="db-recent-card">
                {/* Card header */}
                <div className="db-recent-header">
                    <h2 className="db-recent-title">Recent projects</h2>
                    <button
                        className="db-recent-view-all"
                        type="button"
                        onClick={handleViewAllProjects}
                    >
                        View all <ArrowRight size={14} />
                    </button>
                </div>

                {/* Table or empty state */}
                {projects.length === 0
                    ? (
                        <div className="db-empty-state">
                            <FolderFill className="db-empty-state-icon" />
                            <span className="db-empty-state-text">
                                No projects yet. Create one to get started.
                            </span>
                        </div>
                    )
                    : (
                        <div className="db-recent-table-wrap" role="region" aria-label="Recent projects" tabIndex={0}>
                            <table className="db-recent-table" data-responsive-cards="true">
                                <thead>
                                    <tr>
                                        <th>Project</th>
                                        {showOrganizationColumn && (
                                            <th>Organization</th>
                                        )}
                                        <th className="db-col-center">Docs</th>
                                        <th className="db-col-center">Jobs</th>
                                        <th className="db-col-center">Test Cases</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projects.map((project) => renderProjectRow(project))}
                                </tbody>
                            </table>
                        </div>
                    )
                }
            </div>
        );
    }

    /* ---------- Main render ---------- */

    /* Show loading spinner while fetching */
    if (loading)
    {
        return (
            <div className="db-page">
                {renderLoading()}
            </div>
        );
    }

    return (
        <div className="db-page">
            {/* Error alert */}
            {error && (
                <Alert variant="danger" className="db-error">
                    {error}
                </Alert>
            )}

            {/* Info banner */}
            {renderInfoBanner()}

            {/* Greeting header + Create Project */}
            {renderHeader()}

            {/* Stats cards */}
            {renderStatsCards()}

            {/* Recent projects table */}
            {renderRecentProjects()}
        </div>
    );
}

/* ---------- Export ---------- */

export default Dashboard;
